"use client";
import { motion } from "framer-motion";
import {
  Bot,
  Users2,
  Plug,
  Cpu,
  ShieldAlert,
  Gamepad2,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Bot,
    title: "Telegram Bot Development",
    desc: "End-to-end design and engineering of custom Telegram bots — from concept to production.",
  },
  {
    icon: Users2,
    title: "Community Automation",
    desc: "Onboarding, welcome flows, polls, reminders and engagement loops that scale with your group.",
  },
  {
    icon: Plug,
    title: "Custom Bot Integrations",
    desc: "Bridge Telegram with your APIs, databases, dashboards and third-party tools.",
  },
  {
    icon: Cpu,
    title: "AI-Powered Telegram Tools",
    desc: "Smart assistants, content generators and conversational agents — powered by modern LLMs.",
  },
  {
    icon: ShieldAlert,
    title: "Group Moderation Systems",
    desc: "Anti-spam, auto-mod, captcha, raid protection and granular admin controls.",
  },
  {
    icon: Gamepad2,
    title: "Game & Engagement Bots",
    desc: "Word games, trivia, quizzes and reward systems engineered for daily retention.",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
        >
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#00E5FF] mb-5" data-testid="services-label">
              Services
            </p>
            <h2
              className="font-display text-4xl md:text-5xl font-light tracking-tight"
              data-testid="services-heading"
            >
              Everything you need to launch{" "}
              <span className="gradient-text font-medium">a great Telegram bot</span>.
            </h2>
          </div>
          <p className="text-neutral-400 md:max-w-sm">
            Six focused offerings. Each one production-grade, observably reliable and
            tailored to your community.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <motion.a
              key={i}
              href="#contact"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              className="group relative rounded-2xl bg-[#0A0A0A] border border-white/[0.08] p-7 hover:-translate-y-1 hover:border-white/20 transition-all duration-300 overflow-hidden"
              data-testid={`service-card-${i}`}
            >
              {/* Soft hover glow */}
              <div className="pointer-events-none absolute -top-24 -right-24 h-48 w-48 rounded-full bg-[#007AFF]/0 group-hover:bg-[#007AFF]/15 blur-3xl transition-all duration-500" />

              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center group-hover:border-[#00E5FF]/40 transition-colors">
                  <s.icon className="h-5 w-5 text-[#7CCBF1] group-hover:text-[#00E5FF] transition-colors" />
                </div>
                <h3 className="mt-6 font-display text-xl font-medium">{s.title}</h3>
                <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
                  {s.desc}
                </p>
                <div className="mt-6 flex items-center gap-1.5 text-xs uppercase tracking-wider text-neutral-500 group-hover:text-white transition-colors">
                  Get started
                  <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
